import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { StatusChip } from "@/components/Badges";
import { Link } from "react-router-dom";
import { exportCsv } from "@/lib/csv";
import { Download } from "lucide-react";
import { MyKRA } from "@/pages/admin/AdminWidgets";

export default function AssignmentQueue() {
  const { t } = useLang();
  const [pending, setPending] = useState([]);
  const [missing, setMissing] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [selection, setSelection] = useState({});
  const [msg, setMsg] = useState("");

  const load = async () => {
    const [p, m, i] = await Promise.all([
      api.get("/pos", { params: { status: "Pending Inspector Assignment" } }),
      api.get("/pos", { params: { status: "Draft - ETA Missing" } }),
      api.get("/users/inspectors"),
    ]);
    setPending(p.data);
    setMissing(m.data);
    setInspectors(i.data);
  };
  useEffect(() => { load(); }, []);

  const assign = async (poId) => {
    const insp = selection[poId];
    if (!insp) return;
    const inspector = inspectors.find((x) => x.id === insp);
    if (inspector && inspector.active_loom_count >= 40) {
      if (!window.confirm(`${inspector.name} already has ${inspector.active_loom_count} looms. Continue?`)) return;
    }
    setMsg("");
    try {
      await api.patch(`/pos/${poId}`, { assigned_inspector_id: insp });
      setMsg(`Assigned. PO moved to Issued.`);
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed");
    }
  };

  const doExport = () => {
    exportCsv(
      [...pending, ...missing],
      [
        { key: "po_number", label: "PO Number" },
        { key: "weaver_name", label: "Weaver" },
        { key: "status", label: "Status" },
        { key: "eta", label: "ETA", format: (v) => (v ? new Date(v).toLocaleDateString() : "") },
        { key: "line_item_count", label: "Line Items" },
        { key: "product_category", label: "Category" },
      ],
      "assignment_queue.csv"
    );
  };

  return (
    <div>
      <div className="eyebrow">{t("nav_assignment_queue")}</div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold" style={{ color: "var(--brand-navy)" }}>{t("pending_inspector_assignment")}</h1>
        <button data-testid="aq-export-btn" onClick={doExport} className="h-10 px-3 rounded-md border border-[#1B3A5C]/15 text-sm flex items-center gap-1">
          <Download size={14} /> {t("export_csv")}
        </button>
      </div>
      <div className="flex gap-4 text-sm text-[#4A627A] mt-1">
        <span data-testid="pending-count">Pending: <b>{pending.length}</b></span>
        <span data-testid="missing-eta-count">Missing ETA: <b>{missing.length}</b></span>
        <span>Total needing assignment: <b>{pending.length + missing.length}</b></span>
      </div>
      {msg && <div className="mt-3 text-sm p-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800">{msg}</div>}

      <div className="mt-4"><MyKRA /></div>

      <div className="mt-4 overflow-x-auto hag-scroll">
        <table className="w-full text-sm hag-surface">
          <thead className="bg-[#FAF6F0] text-[#4A627A] uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left p-3">{t("po_number")}</th>
              <th className="text-left p-3">{t("weaver_name")}</th>
              <th className="text-left p-3">{t("eta")}</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">{t("assigned_inspector")}</th>
              <th className="text-left p-3"></th>
            </tr>
          </thead>
          <tbody>
            {pending.map((p) => (
              <tr key={p.id} data-testid={`assign-row-${p.po_number}`} className="border-t border-[#1B3A5C]/5">
                <td className="p-3 font-semibold"><Link to={`/po/${p.id}`} className="text-[#1B3A5C]">{p.po_number}</Link></td>
                <td className="p-3">{p.weaver_name}</td>
                <td className="p-3">{p.eta ? new Date(p.eta).toLocaleDateString() : "—"}</td>
                <td className="p-3"><StatusChip status={p.status} /></td>
                <td className="p-3">
                  <select
                    data-testid={`insp-select-${p.po_number}`}
                    value={selection[p.id] || ""}
                    onChange={(e) => setSelection((s) => ({ ...s, [p.id]: e.target.value }))}
                    className="h-10 border border-[#1B3A5C]/15 rounded-md px-2 text-sm"
                  >
                    <option value="">— select —</option>
                    {inspectors.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name} (Zone {i.zone || "?"}, {i.active_loom_count} looms)
                      </option>
                    ))}
                  </select>
                </td>
                <td className="p-3">
                  <button
                    data-testid={`assign-btn-${p.po_number}`}
                    disabled={!selection[p.id]}
                    onClick={() => assign(p.id)}
                    className="h-10 px-4 rounded-md font-semibold text-white disabled:opacity-50"
                    style={{ background: "var(--brand-navy)" }}
                  >
                    {t("assign")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
